import React, { useState } from 'react';
import './ProductCard.css';

const ProductCard = ({ product, viewMode = 'grid', onProductClick }) => {
  const [isFavorite, setIsFavorite] = useState(false);
  const [isInCart, setIsInCart] = useState(false);

  const {
    id,
    name,
    category,
    description,
    price,
    oldPrice,
    image,
    images, // Add this to destructuring
    rating,
    reviews,
    stock,
    stockCount,
    badge,
    features = []
  } = product;

// Get the display images - handle both old and new formats
const getDisplayImages = () => {
  // New format: images array
  if (Array.isArray(images) && images.length > 0) {
    return images;
  }
  // Old format: single image object
  if (image) {
    return [image];
  }
  return [];
};

const displayImages = getDisplayImages();
const primaryImage = displayImages[0];

// Helper function to get image URL from image object or string
const getImageUrl = (img) => {
  if (!img) return '/placeholder-image.jpg'; // Add a placeholder
  if (typeof img === 'object' && img.url) return img.url;
  if (typeof img === 'string') return img;
  return '/placeholder-image.jpg';
};

  // Format price with spaces for thousands
  const formatPrice = (price) => {
    return price?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') || '0';
  };

  // Calculate discount percentage
  const discount = oldPrice ? Math.round(((oldPrice - price) / oldPrice) * 100) : 0;

  // Render star rating
  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 !== 0;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<span key={i} className="star">★</span>);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<span key={i} className="star">★</span>);
      } else {
        stars.push(<span key={i} className="star empty">★</span>);
      }
    }
    return stars;
  };

  // Get stock status text and class
  const getStockInfo = () => {
    switch (stock) {
      case 'in-stock':
        return { text: 'En stock', class: 'in-stock' };
      case 'low-stock':
        return { text: `Stock faible `, class: 'low-stock' };
      case 'out-of-stock':
        return { text: 'Rupture de stock', class: 'out-of-stock' };
      default:
        return { text: 'En stock', class: 'in-stock' };
    }
  };

  const stockInfo = getStockInfo();

  // Get badge class based on badge type
  const getBadgeClass = () => {
    switch (badge?.toLowerCase()) {
      case 'nouveau':
      case 'new':
        return 'product-badge--new';
      case 'promo':
      case 'sale':
        return 'product-badge--sale';
      case 'hot':
        return 'product-badge--hot';
      case 'bestseller':
        return 'product-badge--stock';
      default:
        return 'product-badge--stock';
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    setIsInCart(true);
    console.log(`Added ${name} to cart`);
  };

  const handleFavorite = (e) => {
    e.stopPropagation();
    setIsFavorite(!isFavorite);
  };

  const handleCompare = (e) => {
    e.stopPropagation();
    console.log(`Compare ${name}`);
  };

  const handleCardClick = () => {
    if (onProductClick) {
      onProductClick(id);
    }
  };

  return (
    <div
      className={`product-card-page ${viewMode === 'list' ? 'list-view' : ''}`}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      {/* Badges */}
      <div className="product-card-page__badges">
        {badge && (
          <div className={`product-badge ${getBadgeClass()}`}>
            {badge}
          </div>
        )}
        {stock === 'low-stock' && (
          <div className="product-badge product-badge--low-stock">
            Stock faible
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="product-card-page__actions">
        <button
          className={`action-btn ${isFavorite ? 'active' : ''}`}
          onClick={handleFavorite}
          title={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>

        <button
          className="action-btn"
          onClick={handleCompare}
          title="Comparer"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="16 3 21 3 21 8" />
            <line x1="4" y1="20" x2="21" y2="3" />
            <polyline points="21 16 21 21 16 21" />
            <line x1="15" y1="15" x2="21" y2="21" />
            <line x1="4" y1="4" x2="9" y2="9" />
          </svg>
        </button>
      </div>

      {/* Image Container */}
      <div className="product-card-page__image-container">
        <img
          src={getImageUrl(primaryImage)}
          alt={name}
          className="product-card-page__image"
          loading="lazy"
        />

        {/* Image indicators for multiple images */}
        {displayImages.length > 1 && (
          <div className="product-card-page__image-indicators">
            {displayImages.slice(0, 5).map((_, idx) => (
              <span key={idx} className="image-indicator"></span>
            ))}
          </div>
        )}

        {/* Quick View Overlay */}
        <div className="product-card-page__quick-view">
          <button className="quick-view-btn" onClick={handleCardClick}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            Voir rapidement
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="product-card-page__content">
        {/* Category */}
        <div className="product-card-page__category">{category}</div>

        {/* Title */}
        <h3 className="product-card-page__title">{name}</h3>

        {/* Description */}
        <p className="product-card-page__description">{description}</p>

        {/* Features */}
        {features.length > 0 && (
          <div className="product-card-page__features">
            {features.slice(0, 2).map((feature, index) => (
              <div key={index} className="feature-tag2">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {feature}
              </div>
            ))}
          </div>
        )}

        {/* Rating */}
        <div className="product-card-page__rating">
          <div className="rating-stars">
            {renderStars()}
          </div>
          <span className="rating-count">({reviews || 0})</span>
        </div>

        {/* Availability */}
        <div className="product-card-page__availability">
          <div className={`availability-dot ${stockInfo.class}`}></div>
          <span className={`availability-text ${stockInfo.class}`}>
            {stockInfo.text}
          </span>
        </div>

        {/* Footer */}
        <div className="product-card-page__footer">
          <div className="product-card-page__price-wrapper">
            <div className="product-card-page__price">
              {formatPrice(price)} DA
            </div>
            {oldPrice && (
              <div className="product-card-page__old-price">
                {formatPrice(oldPrice)} DA
                {discount > 0 && (
                  <span className="product-card-page__discount">-{discount}%</span>
                )}
              </div>
            )}
          </div>


        </div>
      </div>
    </div>
  );
};

export default ProductCard;